import React, { useState, useEffect } from 'react';
import { 
    Settings, UserCog, LogOut, Database, Trash2, Download, 
    Lock, CreditCard, AlertTriangle, Eye, EyeOff, HelpCircle, ExternalLink 
} from 'lucide-react';

// Firebase imports for auth
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

// Shared UI
import { 
    AppCard, AppButton, SectionTitle, InfoCard, AppInput 
} from '../../components/SharedUI';

// --- SUB-COMPONENTS ---

const PasswordChangeModal = ({ isOpen, onClose, user, setNotification }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [isChanging, setIsChanging] = useState(false);

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswords(false);
    };

    const handlePasswordChange = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setNotification({ type: 'error', message: 'All fields are required.' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setNotification({ type: 'error', message: 'New passwords do not match.' });
            return;
        }

        if (newPassword.length < 6) {
            setNotification({ type: 'error', message: 'New password must be at least 6 characters.' });
            return;
        }

        setIsChanging(true);
        try {
            // Reauthenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);
            
            setNotification({ type: 'success', message: 'Password updated successfully!' });
            resetForm();
            onClose();
        } catch (error) {
            console.error('Password change error:', error);
            if (error.code === 'auth/wrong-password') {
                setNotification({ type: 'error', message: 'Current password is incorrect.' });
            } else if (error.code === 'auth/weak-password') {
                setNotification({ type: 'error', message: 'New password is too weak.' });
            } else {
                setNotification({ type: 'error', message: 'Failed to update password. Please try again.' });
            }
        } finally {
            setIsChanging(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-md w-full">
                <h3 className="text-xl font-bold font-poppins text-white mb-4 flex items-center gap-2">
                    <Lock size={20} />
                    Change Password
                </h3>
                <div className="space-y-4">
                    <div className="relative">
                        <AppInput
                            type={showPasswords ? "text" : "password"}
                            placeholder="Current Password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                    </div>
                    <div className="relative">
                        <AppInput
                            type={showPasswords ? "text" : "password"}
                            placeholder="New Password (min 6 characters)"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    <div className="relative">
                        <AppInput
                            type={showPasswords ? "text" : "password"}
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-300 transition-colors"
                    >
                        {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPasswords ? 'Hide' : 'Show'} passwords
                    </button>
                </div>
                <div className="flex justify-end gap-4 mt-8">
                    <AppButton onClick={() => { resetForm(); onClose(); }} variant="secondary" disabled={isChanging}>
                        Cancel
                    </AppButton>
                    <AppButton onClick={handlePasswordChange} disabled={isChanging}>
                        {isChanging ? 'Updating...' : 'Update Password'}
                    </AppButton>
                </div>
            </AppCard>
        </div>
    );
};

const SubscriptionModal = ({ isOpen, onClose, user, userProfile, setNotification }) => {
    const [isCancelling, setIsCancelling] = useState(false);
    const [isManaging, setIsManaging] = useState(false);

    const handleCancelSubscription = async () => {
        if (!userProfile?.subscriptionId) {
            setNotification({ type: 'error', message: 'No active subscription found.' });
            return;
        }

        setIsCancelling(true);
        try {
            const response = await fetch('/api/cancel-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscriptionId: userProfile.subscriptionId })
            });

            const data = await response.json();

            if (response.ok) {
                setNotification({ type: 'success', message: 'Subscription cancelled successfully.' });
                onClose();
            } else {
                setNotification({ type: 'error', message: data.error || 'Failed to cancel subscription.' });
            }
        } catch (error) {
            console.error('Subscription cancellation error:', error);
            setNotification({ type: 'error', message: 'Failed to cancel subscription. Please try again.' });
        } finally {
            setIsCancelling(false);
        }
    };

    const handleManageBilling = async () => {
        console.log('🔍 Debug - userProfile:', userProfile); // Debug log
        
        if (!userProfile?.customerId) {
            console.log('❌ No customerId found in userProfile'); // Debug log
            
            // Try to find the customer using subscription ID if available
            if (userProfile?.subscriptionId) {
                setNotification({ type: 'info', message: 'Looking up your billing information...' });
                try {
                    const lookupResponse = await fetch('/api/lookup-customer', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                            subscriptionId: userProfile.subscriptionId,
                            userId: user.uid 
                        })
                    });
                    
                    if (lookupResponse.ok) {
                        const lookupData = await lookupResponse.json();
                        // The lookup API will update the user profile, so retry after a moment
                        setTimeout(() => {
                            setNotification({ type: 'success', message: 'Billing information found! Please try again.' });
                        }, 2000);
                        return;
                    }
                } catch (error) {
                    console.error('Customer lookup failed:', error);
                }
            }
            
            setNotification({ type: 'error', message: 'No billing information found. Please contact support or try upgrading again.' });
            return;
        }

        console.log('🏦 Starting billing portal session for customer:', userProfile.customerId); // Debug log
        setIsManaging(true);
        
        try {
            const response = await fetch('/api/create-portal-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ customerId: userProfile.customerId })
            });

            console.log('📡 Portal API response status:', response.status); // Debug log
            const data = await response.json();
            console.log('📡 Portal API response data:', data); // Debug log

            if (response.ok) {
                // Redirect to Stripe customer portal
                console.log('✅ Opening Stripe portal:', data.url); // Debug log
                window.open(data.url, '_blank');
            } else {
                console.log('❌ Portal API error:', data); // Debug log
                setNotification({ type: 'error', message: data.error || 'Failed to access billing portal.' });
            }
        } catch (error) {
            console.error('❌ Billing portal error:', error);
            setNotification({ type: 'error', message: 'Failed to access billing portal. Please try again.' });
        } finally {
            setIsManaging(false);
        }
    };

    const handleManageBillingFallback = () => {
        // Direct link to actual Stripe billing portal
        const stripeCustomerUrl = `https://dashboard.stripe.com/p/login`;
        window.open(stripeCustomerUrl, '_blank');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-lg w-full">
                <h3 className="text-xl font-bold font-poppins text-white mb-4 flex items-center gap-2">
                    <CreditCard size={20} />
                    Manage Subscription
                </h3>
                
                {userProfile?.isPro ? (
                    <div className="space-y-6">
                        <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                            <p className="font-semibold text-green-400 mb-2">✓ Pro Subscription Active</p>
                            <p className="text-zinc-300 text-sm">Plan: {userProfile.plan || 'Pro'}</p>
                            <p className="text-zinc-300 text-sm">
                                Credits: {userProfile.creditsRemaining || 0} / {userProfile.monthlyLimit || 50}
                            </p>
                            {userProfile.billingDate && (
                                <p className="text-zinc-300 text-sm">
                                    Next billing: {new Date(userProfile.billingDate.seconds * 1000 || userProfile.billingDate).toLocaleDateString()}
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <AppButton 
                                onClick={handleManageBilling} 
                                disabled={isManaging}
                                className="w-full"
                                icon={CreditCard}
                            >
                                {isManaging ? 'Loading...' : 'Manage Billing & Payment Methods'}
                            </AppButton>

                            <AppButton 
                                onClick={handleManageBillingFallback} 
                                disabled={isManaging}
                                className="w-full"
                                variant="secondary"
                                icon={ExternalLink}
                            >
                                Open Stripe Billing (Direct)
                            </AppButton>

                            <div className="border-t border-zinc-700 pt-4">
                                <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-lg">
                                    <h4 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                                        <AlertTriangle size={16} />
                                        Danger Zone
                                    </h4>
                                    <p className="text-zinc-300 text-sm mb-4">
                                        Cancelling your subscription will downgrade your account to the free tier at the end of your current billing period.
                                    </p>
                                    <AppButton 
                                        onClick={handleCancelSubscription} 
                                        variant="danger" 
                                        disabled={isCancelling}
                                        icon={AlertTriangle}
                                    >
                                        {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
                                    </AppButton>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <p className="text-zinc-400 mb-4">You are currently on the free plan.</p>
                        <AppButton onClick={onClose} variant="secondary">
                            Close
                        </AppButton>
                    </div>
                )}

                <div className="flex justify-end mt-6">
                    <AppButton onClick={onClose} variant="secondary">
                        Close
                    </AppButton>
                </div>
            </AppCard>
        </div>
    );
};

const ResetModal = ({ isOpen, onClose, onReset, isResetting }) => {
    const [selectedOptions, setSelectedOptions] = useState({});

    if (!isOpen) return null;

    // FIX: Update the keys to be associated with an array of fields to reset
    const options = [
        { label: 'Video Analysis & Keyframes', keys: ['video_analysis', 'video_keyframes_urls'] },
        { label: 'Visual Analysis & Posters', keys: ['visual_analysis', 'poster_a', 'poster_b'] },
        { label: 'Budget Planner Report', keys: ['budget_plan'] },
        { label: 'Revenue Forecast Report', keys: ['revenue_forecast'] },
        { label: 'Competitive Intel Report', keys: ['competitor_analysis'] },
        { label: 'Ad Concepts', keys: ['ad_concepts'] },
        { label: 'Script Intelligence Snapshots', keys: ['script_analyses'] },
        { label: 'Press Kit Data', keys: ['press_kit'] },
    ];
    
    const handleToggle = (option) => {
        setSelectedOptions(prev => {
            const newOptions = { ...prev };
            if (newOptions[option.label]) {
                delete newOptions[option.label];
            } else {
                newOptions[option.label] = option.keys;
            }
            return newOptions;
        });
    };

    const handleConfirm = () => {
        const allKeysToReset = Object.values(selectedOptions).flat();
        onReset(allKeysToReset);
        setSelectedOptions({});
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            {/* CHANGED: Added max-h-[90vh] and flex-col to handle small screens */}
            <AppCard className="p-8 max-w-lg w-full max-h-[90vh] flex flex-col">
                
                {/* CHANGED: Added flex-shrink-0 to header elements */}
                <h3 className="text-xl font-bold font-poppins text-white mb-4 flex-shrink-0">Reset Project Data</h3>
                <p className="text-zinc-400 mb-6 flex-shrink-0">Select the data you want to clear from the current project. This action is permanent and cannot be undone.</p>
                
                {/* CHANGED: Added overflow-y-auto and pr-2 to allow scrolling within the list */}
                <div className="space-y-4 overflow-y-auto pr-2 min-h-0">
                    {options.map(option => (
                        <label key={option.label} className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors cursor-pointer ${selectedOptions[option.label] ? 'border-red-500 bg-red-500/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={!!selectedOptions[option.label]}
                                    onChange={() => handleToggle(option)}
                                    className="w-5 h-5 text-red-600 bg-zinc-900 border-zinc-700 rounded focus:ring-red-500"
                                />
                                <span className="font-medium text-white">{option.label}</span>
                            </div>
                        </label>
                    ))}
                </div>

                {/* CHANGED: Added flex-shrink-0 and mt-6 to buttons */}
                <div className="flex justify-end gap-4 mt-6 pt-2 flex-shrink-0">
                    <AppButton onClick={onClose} variant="secondary" disabled={isResetting}>Cancel</AppButton>
                    <AppButton onClick={handleConfirm} variant="danger" disabled={isResetting || Object.keys(selectedOptions).length === 0}>
                        {isResetting ? 'Resetting...' : `Reset ${Object.keys(selectedOptions).length} Item${Object.keys(selectedOptions).length > 1 ? 's' : ''}`}
                    </AppButton>
                </div>
            </AppCard>
        </div>
    );
};

// --- MAIN COMPONENT ---

const SettingsTab = ({ theme, toggleTheme, user, userProfile, handleLogout, setNotification, resetFilmData }) => {
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const handleReset = async (options) => {
        setIsResetting(true);
        await resetFilmData(options);
        setIsResetting(false);
        setIsResetModalOpen(false);
        setNotification({type: 'success', message: 'Project data reset successfully.'});
    };

    return (
        <div className="space-y-8 max-w-4xl mx-auto">
             <SectionTitle icon={Settings}>Settings</SectionTitle>
             
             <InfoCard icon={UserCog} title="User Profile">
                {user ? (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-semibold text-zinc-900 dark:text-white">Email</p>
                                <p className="text-zinc-600 dark:text-zinc-300">{user.email}</p>
                            </div>
                            <AppButton onClick={handleLogout} variant="danger" icon={LogOut}>Log Out</AppButton>
                        </div>
                        
                        <div className="border-t border-zinc-200 dark:border-zinc-700 pt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-semibold text-zinc-900 dark:text-white">Password</p>
                                    <p className="text-zinc-600 dark:text-zinc-300">Update your account password</p>
                                </div>
                                <AppButton onClick={() => setIsPasswordModalOpen(true)} variant="secondary" icon={Lock}>
                                    Change Password
                                </AppButton>
                                <p className="text-zinc-500 text-sm">
                                Questions? Contact us at{' '}
                                <a href="mailto:support@blackbox-is.com" className="text-green-brand hover:underline">
                                    support@blackbox-is.com
                                </a>
                                </p>
                            </div>
                        </div>

                        {userProfile && (
                            <div className="border-t border-zinc-200 dark:border-zinc-700 pt-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-semibold text-zinc-900 dark:text-white">Subscription</p>
                                        <p className="text-zinc-600 dark:text-zinc-300">
                                            {userProfile.isPro ? 'Pro Plan Active' : 'Free Plan'} • 
                                            <span className="ml-1">
                                                {userProfile.creditsRemaining || 0} / {userProfile.monthlyLimit || 5} credits remaining
                                            </span>
                                        </p>
                                    </div>
                                    <AppButton onClick={() => setIsSubscriptionModalOpen(true)} variant="secondary" icon={CreditCard}>
                                        Manage Subscription
                                    </AppButton>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-zinc-500 dark:text-zinc-400">You are not logged in.</p>
                )}
             </InfoCard>
             
             <InfoCard icon={Database} title="Data Management">
                 <div className="space-y-4">
                     <div className="flex items-center justify-between">
                        <div>
                            <p className="font-semibold text-zinc-900 dark:text-white">Reset Project Data</p>
                            <p className="text-zinc-600 dark:text-zinc-300">Clear specific AI-generated reports from the current project.</p>
                        </div>
                        <AppButton onClick={() => setIsResetModalOpen(true)} variant="danger" icon={Trash2}>Reset Data</AppButton>
                     </div>
                     <div className="flex items-center justify-between">
                        <div>
                            <p className="font-semibold text-zinc-900 dark:text-white">Export Project Data</p>
                            <p className="text-zinc-600 dark:text-zinc-300">Download all your project data as a JSON file.</p>
                        </div>
                        <AppButton onClick={() => setNotification({type: 'info', message: 'Export functionality coming soon!'})} variant="secondary" icon={Download}>Export JSON</AppButton>
                     </div>
                 </div>
             </InfoCard>
             {/* --- NEW SUPPORT SECTION --- */}
             <InfoCard icon={HelpCircle} title="Support & Feedback">
                 <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                     <div>
                         <p className="font-semibold text-zinc-900 dark:text-white">Have questions or need help?</p>
                         <p className="text-zinc-600 dark:text-zinc-300 text-sm mt-1">
                             Reach out to our support team for assistance with your account or to report bugs.
                         </p>
                     </div>
                     <a 
                        href="mailto:support@blackbox-is.com"
                        className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white rounded-lg transition-colors font-medium text-sm border border-zinc-200 dark:border-zinc-700"
                     >
                        <ExternalLink size={16} />
                        support@blackbox-is.com
                     </a>
                 </div>
             </InfoCard>
             
             <ResetModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                onReset={handleReset}
                isResetting={isResetting}
            />

            <PasswordChangeModal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                user={user}
                setNotification={setNotification}
            />

            <SubscriptionModal
                isOpen={isSubscriptionModalOpen}
                onClose={() => setIsSubscriptionModalOpen(false)}
                user={user}
                userProfile={userProfile}
                setNotification={setNotification}
            />
        </div>
    );
};

export default SettingsTab;